import { type Address } from '../address.js';
import {
  type BaseItem,
  type Nullable,
  type NullablePartial,
  type Relationship,
} from '../common.js';
import { type Race } from '../patient.js';
import { type EmploymentStatus, type Sex } from '../previsit.js';

export enum Kind {
  Corporate = 'CORPORATE',
  Employee = 'EMPLOYEE',
  MentalHealth = 'MENTAL_HEALTH',
  Other = 'OTHER',
  PersonalFamily = 'PERSONAL_FAMILY',
  RoiRequester = 'ROI_REQUESTER',
  ThirdParty = 'THIRD_PARTY',
  WorkersComp = 'WORKERS_COMP',
}
export enum OptionalGuarantorFields {
  EmailAddress = 'emailAddress',
  Employer = 'employer',
  EmploymentStatus = 'employmentStatus',
  DateOfBirth = 'dateOfBirth',
  PhoneNumberHome = 'phoneNumberHome',
  Sex = 'sex',
}

type BaseGuarantor = { ehrInformationId: string } & Nullable<{
  ehrId: string;
  firstName: string;
  lastName: string;
  sex: Sex;
  dateOfBirth: Date;
  kind: Kind;
  race: Race[];
  raceRaw: string[];
  ssn4: string;
  relationshipToPatient: Relationship;
  relationshipToPatientRaw: string;
  phoneNumberHome: string;
  phoneNumberMobile: string;
  phoneNumberWork: string;
  emailAddress: string;
  employer: string;
  employmentStatus: EmploymentStatus;
  employmentStatusRaw: string;
  retirementDate: Date;
  addressId: string;
  deletedTimestamp: Date;
}>;

export type CreateValues = NullablePartial<BaseGuarantor>;
export type UpdateValues = Partial<BaseGuarantor>;

export interface Guarantor extends BaseItem, BaseGuarantor {}

export interface GuarantorToExport extends NonNullable<
  Pick<
    Guarantor,
    | 'firstName'
    | 'lastName'
    | 'sex'
    | 'kind'
    | 'dateOfBirth'
    | 'relationshipToPatient'
    | 'phoneNumberHome'
    | 'phoneNumberMobile'
    | 'phoneNumberWork'
    | 'emailAddress'
    | 'employer'
    | 'employmentStatus'
  >
> {
  address: Partial<Address>;
}

export interface PrevisitGuarantor extends Omit<Guarantor, 'ehrInformationId'> {
  ehrInformationId: string | null;
  address: Address | null;
  newlyAdded: boolean;
}

export interface PrevisitGuarantorFormFieldValue {
  guarantors: PrevisitGuarantor[];
  selectedGuarantorId?: string | null;
  isSomeoneElse?: boolean;
}

export interface PrevisitGuarantorVerificationFormFieldValue extends PrevisitGuarantorFormFieldValue {
  someoneElse?: string;
  editedExistingGuarantor?: boolean;
}
